
import { defineComponent } from 'vue'
import InfoIcon from '../common/InfoIcon.vue'
import ShiftDataSidebar from './ShiftDataSidebar.vue'

interface Period {
    id: string
    label: string
    date: Date
    salon: string
    channel: string
    administratorName?: string
}

interface Shift {
    id: string
    planId: string
    branchId?: string
    channelId?: string
    [key: string]: any
}

interface Metric {
    id: string
    name: string
    description?: string
    type: 'currency' | 'percentage' | 'number' | 'count' | 'text' | 'decimal' | 'chart'
    hasTooltip?: boolean
    values: Record<string, number | string>
    chartType?: string
    chartMetric?: string
    norm?: number // Норма (средняя арифметическая)
    isBold?: boolean // Жирный шрифт для важных строк
}

export default defineComponent({
    name: 'MetricsTable',
    components: {
        InfoIcon,
        ShiftDataSidebar,
    },
    props: {
        periods: {
            type: Array as () => Period[],
            default: () => [],
        },
        metrics: {
            type: Array as () => Metric[],
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        availableSalons: {
            type: Array,
            default: () => [
                { id: 'salon-a', name: 'Салон А' },
                { id: 'salon-b', name: 'Салон Б' },
            ],
        },
        availableChannels: {
            type: Array,
            default: () => [
                { id: 'channel-a', name: 'Канал А' },
                { id: 'channel-b', name: 'Канал Б' },
            ],
        },
        plans: {
            type: Array as () => any[],
            default: () => [],
        },
        shifts: {
            type: Array as () => Shift[],
            default: () => [],
        },
        canEditShift: {
            type: Function,
            default: () => true,
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
        canCreateShifts: {
            type: Boolean,
            default: true,
        },
        projectId: {
            type: String,
            required: true,
        },
    },
    emits: ['cell-click', 'shift-save', 'shift-delete', 'add-new-shift'],
    data() {
        return {
            selectedChartMetric: 'kpi_performance',
            showShiftSidebar: false,
            selectedShiftData: null as any,
            isAddShiftColumnHovered: false,
            isDraggingTable: false,
            dragMoved: false,
            dragStartX: 0,
            dragScrollStartLeft: 0,
        }
    },
    computed: {
        // Показывать "нет данных" только когда все фильтры на "все" и нет данных для отображения
        shouldShowNoDataMessage() {
            const hasNoPeriods = !(this as any).periods || (this as any).periods.length === 0
            const hasNoMetrics = !(this as any).metrics || (this as any).metrics.length === 0
            const filters = (this as any).filters
            const allFiltersOnAll = !filters || (!filters.branch && !filters.channel)
            return hasNoPeriods && hasNoMetrics && allFiltersOnAll
        },
        // Проверяем, нужно ли показывать строку с администратором
        showAdministratorRow() {
            // Показываем строку только если фильтр администратора не задан или пустой
            // (что означает "Все администраторы")
            const filters = (this as any).filters
            return (
                !filters ||
                !filters.administrator ||
                filters.administrator === '' ||
                filters.administrator === 'all'
            )
        },
        // Фильтруем метрики, исключая график и начисления
        nonChartMetrics() {
            const accrualIds = ['daily_revenue_bonus', 'kpi_bonus', 'fixed_part', 'total_accruals']
            return (this as any).metrics.filter(
                (metric: Metric) => metric.type !== 'chart' && !accrualIds.includes(metric.id)
            )
        },
        // Метрики начислений
        accrualsMetrics() {
            const accrualIds = ['daily_revenue_bonus', 'kpi_bonus', 'fixed_part', 'total_accruals']
            return (this as any).metrics.filter((metric: Metric) => accrualIds.includes(metric.id))
        },
        // Находим метрику для графика на основе выбранной опции
        chartMetric() {
            const selectedMetric = (this as any).selectedChartMetric

            // Если выбрана задача из плана
            if (selectedMetric && selectedMetric.startsWith('task_')) {
                const taskId = selectedMetric.replace('task_', '')
                const activePlan = (this as any).activePlan

                if (activePlan && activePlan.tasks) {
                    const task = activePlan.tasks.find((t: any) => t.id === taskId)
                    if (task) {
                        // Создаем метрику для задачи из данных смен
                        const values: { [key: string]: number } = {}
                        ;(this as any).periods.forEach((period: any) => {
                            values[period.id] = (this as any).getTaskValueFromShifts(
                                selectedMetric,
                                period.id
                            )
                        })

                        return {
                            id: selectedMetric,
                            name: task.name,
                            description: task.description || '',
                            values: values,
                            type: 'number', // Задачи обычно имеют числовой тип
                            unit: task.unit || '',
                        }
                    }
                }
            }

            // Находим соответствующую метрику по ID
            const metric = (this as any).metrics.find((m: Metric) => m.id === selectedMetric)

            if (metric) {
                return {
                    ...metric,
                    name: 'Показать на графике',
                }
            }

            // Fallback на первую доступную метрику
            const fallback = (this as any).metrics[0] || null
            return fallback
        },
        // План, выбранный в фильтре "План"
        selectedPlan() {
            const filters = (this as any).filters
            const selectedPlanId = filters?.plan
            if (!selectedPlanId) {
                return null
            }
            return (this as any).plans.find((p: any) => p.id === selectedPlanId) || null
        },
        // Рабочий план в таблице: выбранный в фильтре, иначе первый активный
        activePlan() {
            return (
                (this as any).selectedPlan ||
                (this as any).plans.find((p: any) => p.isActive === true) ||
                null
            )
        },
        // Список салонов из активного плана (фильтруется по выбранному филиалу)
        planBranches() {
            const plan = (this as any).activePlan
            if (!plan || !plan.branchIds || plan.branchIds.length === 0) {
                return []
            }
            
            // Если выбран конкретный филиал в фильтре, показываем только его
            const filters = (this as any).filters
            if (filters && filters.branch && filters.branch !== '' && filters.branch !== 'all') {
                // Проверяем, что выбранный филиал есть в списке филиалов плана
                if (plan.branchIds.includes(filters.branch)) {
                    return [filters.branch]
                }
                // Если выбранного филиала нет в плане, возвращаем пустой массив
                return []
            }
            
            // Если филиал не выбран, показываем все филиалы из плана
            return plan.branchIds
        },
        // Базовые фиксированные ширины колонок
        metricColWidth(): number {
            return 371
        },
        weightColWidth(): number {
            return 50
        },
        branchColWidth(): number {
            return 90
        },
        // Смещения для липких колонок
        stickyWeightLeft(): number {
            return (this as any).metricColWidth
        },
        stickyBranchLefts(): number[] {
            const count = (this as any).planBranches.length || 0
            const base = (this as any).metricColWidth + (this as any).weightColWidth
            const step = (this as any).branchColWidth
            return Array.from({ length: count }, (_, idx) => base + idx * step)
        },
        stickyAccrualPeriodLeft(): number {
            const count = (this as any).planBranches.length || 0
            return (
                (this as any).metricColWidth +
                (this as any).weightColWidth +
                count * (this as any).branchColWidth
            )
        },
        // Доступные салоны с названиями
        availableBranchesWithNames() {
            const branches = (this as any).planBranches
            const availableSalons = (this as any).availableSalons || []
            return branches.map((branchId: string) => {
                const salon = availableSalons.find((s: any) => s.id === branchId)
                return {
                    id: branchId,
                    name: salon ? salon.name : branchId,
                }
            })
        },
        // Варианты для выбора графика (включая задачи из плана)
        chartOptions() {
            const options = [
                { value: 'kpi_performance', label: 'Выполнение дневного KPI' },
                { value: 'kpi_bonus', label: 'Бонус за выполнение KPI' },
            ]

            // Добавляем задачи из активного плана
            if ((this as any).activePlan && (this as any).activePlan.tasks) {
                ;(this as any).activePlan.tasks.forEach((task: any) => {
                    options.push({
                        value: `task_${task.id}`,
                        label: task.name,
                    })
                })
            }

            // Добавляем остальные стандартные варианты
            options.push(
                { value: 'dailyRevenue', label: 'Дневная выручка' },
                { value: 'daily_revenue_bonus', label: 'Премия за уровень дневной выручки' }
            )

            return options
        },
        // Выбранная опция графика для tooltip
        selectedChartOption() {
            return (this as any).chartOptions.find(
                (option: any) => option.value === (this as any).selectedChartMetric
            )
        },
        // Метрики с нормами (до "Дневная выручка")
        metricsWithNorms() {
            return (this as any).metrics.filter(
                (metric: Metric) =>
                    metric.norm !== undefined && metric.id !== 'dailyRevenue' && metric.type !== 'chart'
            )
        },
        // Генерируем метки шкалы на основе типа метрики
        scaleLabels() {
            if (!(this as any).chartMetric) return []

            const metric = (this as any).chartMetric

            // Собираем все значения, включая нормы для всех периодов
            const allValues: number[] = []

            // Добавляем фактические значения
            if (metric.id && metric.id.startsWith('task_')) {
                allValues.push(
                    ...(this as any).periods.map((p: any) =>
                        (this as any).getTaskValueFromShifts(metric.id, p.id)
                    )
                )
            } else {
                allValues.push(...(Object.values(metric.values) as number[]))
            }

            // Добавляем нормы для всех периодов
            ;(this as any).periods.forEach((p: any) => {
                const periodNorm = (this as any).getNormForPeriod(metric, p)
                if (periodNorm > 0) {
                    allValues.push(periodNorm)
                }
            })

            const maxValue = Math.max(...allValues, 0)

            if (metric.type === 'percentage') {
                return ['125%', '100%', '75%', '50%', '25%']
            } else if (metric.type === 'currency') {
                // Для валюты: используем точные значения без округления
                if (maxValue <= 0) {
                    return ['0P', '0P', '0P', '0P', '0P']
                }

                const step = maxValue / 6 // делим максимальное значение на 6
                const labels = []
                for (let i = 5; i >= 1; i--) {
                    const value = Math.round(step * i * 100) / 100 // округляем до 2 знаков после запятой
                    labels.push(`${value.toLocaleString('ru-RU')}P`)
                }
                return labels
            } else {
                // Для чисел: округляем до удобных значений (без 0)
                const step = maxValue / 6
                const labels = []
                for (let i = 5; i >= 1; i--) {
                    const label = maxValue < 20 ? Math.round(step * i * 100) / 100 : Math.round(step * i)
                    labels.push(`${label}`)
                }
                return labels
            }
        },
        // Ширина SVG для линии графика
        chartLineWidth() {
            const cellWidth = 120 // примерная ширина ячейки
            return (this as any).periods.length * cellWidth
        },
        // Путь SVG линии, соединяющей вершины столбцов
        chartLinePath() {
            if (!(this as any).chartMetric || !(this as any).periods.length) return ''

            const metric = (this as any).chartMetric
            const periods = (this as any).periods
            const cellWidth = 120 // ширина ячейки графика
            const chartHeight = 124 // высота графика в пикселях
            const barWidth = 20 // ширина столбца

            // Создаем массив точек для линии
            const points = periods.map((period: any, index: number) => {
                const barHeight = (this as any).getBarHeight(metric, period)
                const x = index * cellWidth + cellWidth / 2 // центр ячейки
                const y = chartHeight - (barHeight * chartHeight) / 100 // переводим проценты в пиксели
                return { x, y }
            })

            // Создаем плавную кривую Безье через все точки
            if (points.length === 0) return ''
            if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

            // Начинаем путь
            let path = `M ${points[0].x} ${points[0].y}`

            // Создаем плавную кривую через контрольные точки
            for (let i = 0; i < points.length - 1; i++) {
                const current = points[i]
                const next = points[i + 1]

                // Контрольные точки для плавной кривой
                const controlPoint1X = current.x + (next.x - current.x) / 3
                const controlPoint1Y = current.y
                const controlPoint2X = current.x + (2 * (next.x - current.x)) / 3
                const controlPoint2Y = next.y

                // Кубическая кривая Безье
                path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${next.x} ${next.y}`
            }

            return path
        },
        // Получаем норму для текущей метрики графика
        chartMetricNorm() {
            const metric = (this as any).chartMetric
            if (!metric) return 0

            // Для процентных метрик KPI - норма 100%
            if (metric.id === 'kpi_performance') {
                return 100
            }

            // Для задач из плана - норма из плана
            if (metric.id && metric.id.startsWith('task_')) {
                const activePlan = (this as any).activePlan
                if (activePlan && activePlan.tasks) {
                    const taskId = metric.id.replace('task_', '')
                    const task = activePlan.tasks.find((t: any) => t.id === taskId)
                    if (task) {
                        // Для задач норма хранится в структуре norms[branchId][channelId]
                        // Берем первую доступную норму для отображения на графике
                        if (task.norms) {
                            const firstBranch = Object.keys(task.norms)[0]
                            if (firstBranch && task.norms[firstBranch]) {
                                const firstChannel = Object.keys(task.norms[firstBranch])[0]
                                if (firstChannel) {
                                    const norm = task.norms[firstBranch][firstChannel] || 0
                                    return norm
                                }
                            }
                        }
                        // Fallback на старое поле norm
                        const norm = task.norm || 0
                        return norm
                    }
                }
                return 0
            }

            // Для обычных задач (не с префиксом task_) - ищем в метриках
            const metricData = (this as any).metrics.find((m: Metric) => m.id === metric.id)
            if (metricData && metricData.norm) {
                return metricData.norm
            }

            // Для задач из плана по ID (без префикса task_)
            const activePlan = (this as any).activePlan
            if (activePlan && activePlan.tasks) {
                const task = activePlan.tasks.find((t: any) => t.id === metric.id)
                if (task) {
                    // Для задач норма хранится в структуре norms[branchId][channelId]
                    if (task.norms) {
                        const firstBranch = Object.keys(task.norms)[0]
                        if (firstBranch && task.norms[firstBranch]) {
                            const firstChannel = Object.keys(task.norms[firstBranch])[0]
                            if (firstChannel) {
                                const norm = task.norms[firstBranch][firstChannel] || 0
                                return norm
                            }
                        }
                    }
                    // Fallback на старое поле norm
                    if (task.norm) {
                        return task.norm
                    }
                }
            }

        // Для бонуса за выполнение KPI - рассчитываем из номинала и коэффициента при 100%
            if (metric.id === 'kpi_bonus') {
                const activePlan = (this as any).activePlan

                // Ищем kpiScale в плане и номинал
                if (activePlan && activePlan.kpiScale && activePlan.salary && activePlan.salary.kpiBonus) {
                    // Номинал может быть в разных полях, пробуем все варианты
                    const nominal =
                        activePlan.salary.kpiBonus.nominal ||
                        activePlan.salary.kpiBonus ||
                        activePlan.salary.kpiBonus.nominalValue ||
                        parseFloat(activePlan.salary.kpiBonus.toString()) ||
                        0

                    // Ищем запись с completion: 100 в kpiScale
                    const scaleAt100 = activePlan.kpiScale.find((s: any) => s.completion === 100)

                    if (scaleAt100 && scaleAt100.bonusPercentage) {
                        const bonusPercentage = scaleAt100.bonusPercentage
                        const norm = nominal * (bonusPercentage / 100) // bonusPercentage обычно в процентах
                        return norm
                    }
                }

                // Альтернативный поиск в других полях плана
                if (activePlan && activePlan.kpiScale && activePlan.kpiBonus) {
                    const nominal = activePlan.kpiBonus.nominal || 0

                    // Ищем запись с completion: 100 в kpiScale
                    const scaleAt100 = activePlan.kpiScale.find((s: any) => s.completion === 100)

                    if (scaleAt100 && scaleAt100.bonusPercentage) {
                        const bonusPercentage = scaleAt100.bonusPercentage
                        const norm = nominal * (bonusPercentage / 100) // bonusPercentage обычно в процентах
                        return norm
                    }
                }

                // Если не нашли в метриках, проверяем dailyKpiBonus из плана
                if (activePlan && activePlan.dailyKpiBonus) {
                    const norm = parseFloat(activePlan.dailyKpiBonus) || 0
                    return norm
                }

                return 0
            }

            // Для дневной выручки - норма из плана по филиалу
            if (metric.id === 'dailyRevenue') {
                const activePlan = (this as any).activePlan

                if (activePlan && activePlan.dailyRevenuePlan) {
                    // Берем первую доступную норму для отображения на графике
                    const firstBranch = Object.keys(activePlan.dailyRevenuePlan)[0]
                    if (firstBranch) {
                        const norm = activePlan.dailyRevenuePlan[firstBranch] || 0
                        return norm
                    }
                }

                return 0
            }

            // Для остальных метрик (премия за выручку) - норма не определена
            return 0
        },
        // Позиция линии нормы в процентах от высоты графика
        normLinePosition() {
            const norm = (this as any).chartMetricNorm
            if (norm === 0) return 0

            const metric = (this as any).chartMetric
            if (!metric) return 0

            // Находим максимальное значение для нормализации
            let maxValue = 0
            if (metric.type === 'percentage') {
                maxValue = 150 // максимум для процентов
            } else {
                // Для задач из плана и других метрик
                if (metric.id && metric.id.startsWith('task_')) {
                    maxValue = Math.max(
                        ...(this as any).periods.map((p: any) =>
                            (this as any).getTaskValueFromShifts(metric.id, p.id)
                        )
                    )
                } else {
                    maxValue = Math.max(...(Object.values(metric.values) as number[]), 0)
                }
            }

            if (maxValue === 0) return 0

            // Рассчитываем позицию в процентах от высоты графика
            return Math.min((norm / maxValue) * 100, 100)
        },
        // Проверяем, является ли выбранный период текущим месяцем
        isCurrentMonth() {
            const filters = (this as any).filters
            if (!filters || !filters.period) return false

            const currentDate = new Date()
            const currentMonth = currentDate.getMonth() + 1
            const currentYear = currentDate.getFullYear()

            return filters.period.month === currentMonth && filters.period.year === currentYear
        },
        // Проверяем, выбран ли активный план
        isActivePlanSelected() {
            const filters = (this as any).filters
            const selectedPlan = (this as any).selectedPlan
            if (!filters || !selectedPlan) return false
            return Boolean(selectedPlan.isActive === true)
        },
        // Показывать ли столбец добавления смены
        showAddShiftColumn() {
            return (
                (this as any).isCurrentMonth &&
                (this as any).isActivePlanSelected &&
                (this as any).canCreateShifts
            )
        },
    },
    methods: {
        // Получаем норму для конкретного периода (смены), учитывая филиал и канал
        getNormForPeriod(metric: any, period: any): number {
            if (!metric || !period) return 0

            // Для процентных метрик KPI - норма 100%
            if (metric.id === 'kpi_performance') {
                return 100
            }

            // Получаем данные смены для этого периода
            const shifts = (this as any).shifts
            const shift = shifts.find((s: any) => s.id === period.id)

            if (!shift) {
                return 0
            }

            const branchId = shift.branchId
            const channelId = shift.channelId
            const periodPlan = this.getPlanForShift(period.id) || (this as any).activePlan

            // Для задач из плана - норма из plana по branchId и channelId
            if (metric.id && (metric.id.startsWith('task_') || metric.hasTooltip)) {
                if (periodPlan && periodPlan.tasks) {
                    const taskId = metric.id.startsWith('task_') ? metric.id.replace('task_', '') : metric.id
                    const task = periodPlan.tasks.find((t: any) => t.id === taskId)
                    if (task && task.norms && task.norms[branchId] && task.norms[branchId][channelId]) {
                        const norm = task.norms[branchId][channelId] || 0
                        return norm
                    }
                }
                return 0
            }

            // Для бонуса за выполнение KPI - рассчитываем из номинала и коэффициента при 100%
            if (metric.id === 'kpi_bonus') {
                const activePlan = periodPlan

                // Ищем kpiScale в плане и номинал
                if (activePlan && activePlan.kpiScale && activePlan.salary && activePlan.salary.kpiBonus) {
                    const nominal =
                        activePlan.salary.kpiBonus.nominal ||
                        activePlan.salary.kpiBonus ||
                        activePlan.salary.kpiBonus.nominalValue ||
                        parseFloat(activePlan.salary.kpiBonus.toString()) ||
                        0

                    const scaleAt100 = activePlan.kpiScale.find((s: any) => s.completion === 100)

                    if (scaleAt100 && scaleAt100.bonusPercentage) {
                        const bonusPercentage = scaleAt100.bonusPercentage
                        return nominal * (bonusPercentage / 100)
                    }
                }

                if (activePlan && activePlan.kpiScale && activePlan.kpiBonus) {
                    const nominal = activePlan.kpiBonus.nominal || 0
                    const scaleAt100 = activePlan.kpiScale.find((s: any) => s.completion === 100)

                    if (scaleAt100 && scaleAt100.bonusPercentage) {
                        const bonusPercentage = scaleAt100.bonusPercentage
                        return nominal * (bonusPercentage / 100)
                    }
                }

                return 0
            }

            // Для дневной выручки - норма из плана по филиалу для конкретной смены
            if (metric.id === 'dailyRevenue') {
                const activePlan = periodPlan

                if (activePlan && activePlan.dailyRevenuePlan && activePlan.dailyRevenuePlan[branchId]) {
                    const norm = activePlan.dailyRevenuePlan[branchId] || 0
                    return norm
                }

                return 0
            }

            // Для остальных метрик (премия за выручку) - норма не определена
            return 0
        },
        // Позиция линии нормы в процентах от высоты графика для конкретного периода
        getNormLinePosition(metric: any, period: any): number {
            const norm = this.getNormForPeriod(metric, period)
            if (norm === 0) return 0

            if (!metric) return 0

            // Находим максимальное значение для нормализации
            let maxValue = 0
            if (metric.type === 'percentage') {
                maxValue = 150 // максимум для процентов
            } else {
                // Для задач из плана и других метрик
                // Собираем все значения, включая нормы для всех периодов
                const allValues: number[] = []

                if (metric.id && metric.id.startsWith('task_')) {
                    // Добавляем значения из всех периодов
                    allValues.push(
                        ...(this as any).periods.map((p: any) =>
                            (this as any).getTaskValueFromShifts(metric.id, p.id)
                        )
                    )
                } else {
                    // Добавляем значения из metric.values
                    allValues.push(...(Object.values(metric.values) as number[]))
                }

                // Добавляем нормы для всех периодов, чтобы масштаб графика учитывал линии норм
                ;(this as any).periods.forEach((p: any) => {
                    const periodNorm = this.getNormForPeriod(metric, p)
                    if (periodNorm > 0) {
                        allValues.push(periodNorm)
                    }
                })

                maxValue = Math.max(...allValues, 0)
            }

            if (maxValue === 0) return 0

            // Рассчитываем позицию в процентах от высоты графика
            const position = (norm / maxValue) * 100
            return Math.min(position, 100)
        },
        formatValue(metric: Metric, period: Period): string {
            let value = metric.values[period.id] || 0

            // Если это задача из плана, получаем значение из смен
            if (metric.id && metric.id.startsWith('task_')) {
                value = this.getTaskValueFromShifts(metric.id, period.id)
            }

            // Получаем название задачи для проверки наличия "%"
            let taskName = metric.name || ''
            const periodPlan = this.getPlanForShift(period.id) || (this as any).activePlan
            
            // Если название недоступно и это задача из плана, пытаемся получить из активного плана
            if (!taskName && metric.id && metric.id.startsWith('task_')) {
                if (periodPlan && periodPlan.tasks) {
                    const taskId = metric.id.replace('task_', '')
                    const task = periodPlan.tasks.find((t: any) => t.id === taskId)
                    if (task) {
                        taskName = task.name || ''
                    }
                }
            }

            // Проверяем, есть ли в названии задачи символ "%"
            const hasPercentInName = taskName.includes('%')

            // Если в названии есть "%", отображаем факт как процент от нормы
            if (hasPercentInName) {
                const norm = this.getNormForPeriod(metric, period)
                if (norm > 0) {
                    // Вычисляем процент: (факт / норма) * 100
                    const numValue = Number(value) || 0
                    const numNorm = Number(norm) || 0
                    if (numNorm > 0) {
                        const percentage = Math.round((numValue / numNorm) * 100)
                        return `${percentage}%`
                    }
                }
            }

            switch (metric.type) {
                case 'currency':
                    return new Intl.NumberFormat('ru-RU', {
                        style: 'currency',
                        currency: 'RUB',
                        minimumFractionDigits: 0,
                    }).format(value as number)
                case 'percentage':
                    return `${value}%`
                case 'count':
                    return value.toString()
                case 'decimal':
                    return (value as number).toFixed(1)
                case 'text':
                    return value.toString()
                default:
                    return new Intl.NumberFormat('ru-RU').format(value as number)
            }
        },

        getValueClass(metric: Metric, period: Period): string {
            const value = metric.values[period.id] || 0

            // Логика для определения класса в зависимости от значения
            if (metric.type === 'percentage') {
                const numericValue = typeof value === 'number' ? value : parseFloat(value.toString()) || 0
                if (numericValue >= 100) return 'excellent'
                if (numericValue >= 80) return 'average'
                return 'poor'
            }

            return ''
        },

        getMetricDescription(metric: Metric): string {
            let description = metric.description || ''

            const activePlan = (this as any).activePlan
            if (activePlan && activePlan.tasks) {
                const metricId = metric.id
                let task = activePlan.tasks.find((t: any) => t.id === metricId)

                if (!task && metricId.startsWith('task_')) {
                    const taskId = metricId.replace('task_', '')
                    task = activePlan.tasks.find((t: any) => t.id === taskId)
                }

                if (!task) {
                    task = activePlan.tasks.find((t: any) => t.name === metric.name)
                }

                if (task && task.description) {
                    description = task.description
                }
            }

            return description
        },

        // Обработчик клика по названию метрики для переключения графика
        onMetricNameClick(metric: Metric) {
            if ((this as any).dragMoved) {
                (this as any).dragMoved = false
                return
            }
            // Проверяем, есть ли эта метрика в вариантах графика
            const chartOption = (this as any).chartOptions.find((option: any) => option.value === metric.id)
            if (chartOption) {
                ;(this as any).selectedChartMetric = metric.id
            } else {
                // Если это задача из плана, создаем ID для задачи
                const activePlan = (this as any).activePlan
                if (activePlan && activePlan.tasks) {
                    const task = activePlan.tasks.find((t: any) => t.name === metric.name)
                    if (task) {
                        ;(this as any).selectedChartMetric = `task_${task.id}`
                    }
                }
            }
        },

        // Обработчик изменения селектора графика
        onChartMetricChange() {
            // Метод для будущих расширений, если потребуется дополнительная логика
        },

        // Проверяем, является ли метрика кликабельной (можно ли на неё переключить график)
        isClickableMetric(metric: Metric): boolean {
            // Проверяем, есть ли эта метрика в вариантах графика
            const chartOption = (this as any).chartOptions.find((option: any) => option.value === metric.id)
            if (chartOption) {
                return true
            }

            // Проверяем, если это задача из плана
            const activePlan = (this as any).activePlan
            if (activePlan && activePlan.tasks) {
                const task = activePlan.tasks.find((t: any) => t.name === metric.name)
                if (task) {
                    return true
                }
            }

            return false
        },

        // Проверяем, является ли метрика активной для графика
        isActiveChartMetric(metric: Metric): boolean {
            const selectedMetric = (this as any).selectedChartMetric

            // Прямое совпадение ID
            if (metric.id === selectedMetric) {
                return true
            }

            // Проверяем, если выбрана задача из плана
            if (selectedMetric && selectedMetric.startsWith('task_')) {
                const activePlan = (this as any).activePlan
                if (activePlan && activePlan.tasks) {
                    const taskId = selectedMetric.replace('task_', '')
                    const task = activePlan.tasks.find((t: any) => t.id === taskId)
                    if (task && task.name === metric.name) {
                        return true
                    }
                }
            }

            return false
        },

        getBarHeight(metric: Metric | undefined, period: Period): number {
            if (!metric) return 0

            let value = (metric.values[period.id] as number) || 0

            // Если это задача из плана, получаем данные из смен
            if (metric.id && metric.id.startsWith('task_')) {
                value = this.getTaskValueFromShifts(metric.id, period.id)
            }

            // Находим максимальное значение для нормализации
            let maxValue = 0
            if (metric.type === 'percentage') {
                maxValue = 150
            } else {
                // Собираем все значения, включая нормы для всех периодов
                const allValues: number[] = []

                // Для задач из плана нужно найти максимальное значение из всех смен
                if (metric.id && metric.id.startsWith('task_')) {
                    allValues.push(...this.periods.map(p => this.getTaskValueFromShifts(metric.id, p.id)))
                } else {
                    allValues.push(...this.periods.map(p => (metric.values[p.id] as number) || 0))
                }

                // Добавляем нормы для всех периодов, чтобы масштаб графика учитывал линии норм
                this.periods.forEach((p: any) => {
                    const periodNorm = this.getNormForPeriod(metric, p)
                    if (periodNorm > 0) {
                        allValues.push(periodNorm)
                    }
                })

                maxValue = Math.max(...allValues, 0)
            }

            if (maxValue === 0) return 0

            // Нормализуем высоту (максимум 100%)
            return Math.min((value / maxValue) * 100, 100)
        },

        // Позиция горизонтальной линии шкалы (в процентах от верха)
        getGridLinePosition(index: number): number {
            // 6 линий (0-5), равномерно распределенных
            return ((index + 1) / 6) * 100
        },

        // Получаем значение задачи из смен для конкретного периода
        getTaskValueFromShifts(taskId: string, periodId: string): number {
            const taskIdOnly = taskId.replace('task_', '')
            const shift = this.shifts.find((s: Shift) => s.id === periodId)

            if (!shift || !shift.indicators) return 0

            const value = parseFloat(shift.indicators[taskIdOnly]) || 0
            return value
        },

        // Получаем план по ID
        getPlanById(planId: string): any {
            return this.plans.find((plan: any) => plan.id === planId) || null
        },

        // Получаем план для конкретной смены
        getPlanForShift(shiftId: string): any {
            const shift = this.shifts.find((s: Shift) => s.id === shiftId)
            if (!shift || !shift.planId) return null
            return this.getPlanById(shift.planId)
        },
        // Вес задачи/метрики
        getMetricWeight(metric: Metric): string {
            // Определяем ID задачи без префикса
            const taskId = metric.id
                ? metric.id.startsWith('task_')
                    ? metric.id.replace('task_', '')
                    : metric.id
                : ''

            if (!taskId) return ''

            const plan = this.activePlan
            const task = plan && plan.tasks ? plan.tasks.find((t: any) => t.id === taskId) : null
            const significance = task?.significance ?? task?.weight ?? null
            if (significance !== null && significance !== undefined && significance !== '') {
                return `${significance}%`
            }
            return ''
        },
        // Определяем CSS класс для цвета значения в зависимости от отклонения от нормы
        getValueColorClass(metric: Metric, period: Period): string {
            // Получаем план для конкретной смены
            const plan = this.getPlanForShift(period.id)
            if (!plan || !plan.tasks) return '' // Обычный цвет, если нет плана

            const value =
                typeof metric.values[period.id] === 'number'
                    ? (metric.values[period.id] as number)
                    : parseFloat(metric.values[period.id] as string) || 0

            // Получаем филиал из периода, канал всегда 'general'
            const branchId = period.salon || 'general'
            const channelId = 'general'

            let norm = 0

            // Обрабатываем дневную выручку отдельно
            if (metric.id === 'dailyRevenue') {
                if (plan.dailyRevenuePlan && plan.dailyRevenuePlan[branchId] !== undefined) {
                    norm = plan.dailyRevenuePlan[branchId]
                } else if (plan.generalDailyRevenuePlan) {
                    norm = plan.generalDailyRevenuePlan
                }
            } else {
                // Обрабатываем задачи
                const task = plan.tasks.find((t: any) => t.id === metric.id)
                if (!task || !task.norms) return '' // Обычный цвет, если нет задачи или норм

                // Получаем норму с учетом иерархии: branchId -> channelId -> general
                if (task.norms[branchId] && task.norms[branchId][channelId] !== undefined) {
                    norm = task.norms[branchId][channelId]
                } else if (task.norms['general'] && task.norms['general'][channelId] !== undefined) {
                    norm = task.norms['general'][channelId]
                } else if (task.norms[branchId] && task.norms[branchId]['general'] !== undefined) {
                    norm = task.norms[branchId]['general']
                } else if (task.norms['general'] && task.norms['general']['general'] !== undefined) {
                    norm = task.norms['general']['general']
                }
            }

            if (norm === 0) return ''

            const completionPercentage = (value / norm) * 100

            if (completionPercentage >= 100) {
                return 'above-norm'
            }

            if (completionPercentage < 80) {
                return 'below-norm'
            }
            return ''
        },

        onCellClick(metric: Metric, period: Period) {
            if ((this as any).dragMoved) {
                (this as any).dragMoved = false
                return
            }
            // Не обрабатываем клики по строке графика
            if (metric.type === 'chart') return

            // Находим смену для данного периода
            const shift = (this as any).shifts.find((s: any) => s.id === period.id)

            // Проверяем права доступа на редактирование смены
            if (shift && !(this as any).canEditShift(shift)) {
                // Если нет прав на редактирование, не открываем сайдбар
                return
            }

            // Собираем данные для выбранного столбца
            const columnData = (this as any).metrics.reduce((acc: any, m: Metric) => {
                acc[m.id] = {
                    name: m.name,
                    value: m.values[period.id],
                    type: m.type,
                }
                return acc
            }, {} as Record<string, any>)

            // Эмитим событие с данными столбца (логика обработки в родительском компоненте)
            this.$emit('cell-click', {
                period: period,
                data: columnData,
            })
        },

        closeShiftSidebar() {
            this.showShiftSidebar = false
            this.selectedShiftData = null
        },

        saveShiftData(shiftData: any) {
            this.$emit('shift-save', shiftData)
            this.closeShiftSidebar()
        },

        deleteShiftData(shiftData: any) {
            this.$emit('shift-delete', shiftData)
            this.closeShiftSidebar()
        },

        addNewShift(event: Event) {
            // Предотвращаем всплытие события
            event.stopPropagation()

            // Проверяем наличие опубликованного плана
            if (!this.activePlan) {
                return
            }

            // Создаем данные смены на основе активного плана (как в BeautyAutopilot.vue)
            this.selectedShiftData = {
                date: new Date().toISOString().split('T')[0], // Текущая дата
                salon:
                    this.activePlan.branchIds && this.activePlan.branchIds.length > 0
                        ? this.activePlan.branchIds[0]
                        : '',
                channel:
                    this.activePlan.channelIds && this.activePlan.channelIds.length > 0
                        ? this.activePlan.channelIds[0]
                        : '',
                metrics: {},
                planId: this.activePlan.id,
                // Устанавливаем администратора из фильтра
                administratorId: this.filters?.administrator || '',
            }

            // Открываем сайдбар
            this.showShiftSidebar = true
        },

        onAddShiftColumnHover() {
            this.isAddShiftColumnHovered = true
        },

        onAddShiftColumnLeave() {
            this.isAddShiftColumnHovered = false
        },

        // Drag-to-scroll horizontally without breaking clicks
        getClientX(event: MouseEvent | TouchEvent): number {
            if ((event as TouchEvent).touches && (event as TouchEvent).touches.length > 0) {
                return (event as TouchEvent).touches[0].clientX
            }
            return (event as MouseEvent).clientX
        },
        onDragStart(event: MouseEvent | TouchEvent) {
            if ((event as MouseEvent).button !== undefined && (event as MouseEvent).button !== 0) {
                return
            }
            const wrapper = (this.$refs.tableWrapper as HTMLElement) || null
            if (!wrapper) return
            this.isDraggingTable = true
            this.dragMoved = false
            this.dragStartX = this.getClientX(event)
            this.dragScrollStartLeft = wrapper.scrollLeft
            wrapper.classList.add('grabbing')
        },
        onDragMove(event: MouseEvent | TouchEvent) {
            if (!this.isDraggingTable) return
            const wrapper = (this.$refs.tableWrapper as HTMLElement) || null
            if (!wrapper) return
            const currentX = this.getClientX(event)
            const delta = currentX - this.dragStartX
            if (Math.abs(delta) > 3) {
                this.dragMoved = true
            }
            wrapper.scrollLeft = this.dragScrollStartLeft - delta
        },
        onDragEnd(event: MouseEvent | TouchEvent) {
            if (!this.isDraggingTable) return
            const wrapper = (this.$refs.tableWrapper as HTMLElement) || null
            if (wrapper) {
                wrapper.classList.remove('grabbing')
            }
            this.isDraggingTable = false
            // Если был реальный drag, гасим клик
            if (this.dragMoved && event) {
                if (typeof event.preventDefault === 'function') event.preventDefault()
                if (typeof event.stopPropagation === 'function') event.stopPropagation()
            }
            // Флаг сбросится при первом обработчике клика
        },

        // Получить название салона
        getBranchName(branchId: string): string {
            const branch = (this as any).availableBranchesWithNames.find(
                (b: any) => b.id === branchId
            )
            return branch ? branch.name : branchId
        },
        // Получить норму задачи для конкретного салона
        getTaskNormForBranch(taskId: string, branchId: string): number {
            const taskIdOnly = taskId.replace('task_', '')
            const plan = (this as any).activePlan
            if (!plan || !plan.tasks) return 0

            const task = plan.tasks.find((t: any) => t.id === taskIdOnly)
            if (!task || !task.norms) return 0

            // Пробуем получить норму для конкретного салона, канал всегда 'general'
            const channelId = 'general'

            if (task.norms[branchId] && task.norms[branchId][channelId] !== undefined) {
                return task.norms[branchId][channelId]
            } else if (task.norms['general'] && task.norms['general'][channelId] !== undefined) {
                return task.norms['general'][channelId]
            } else if (task.norms[branchId] && task.norms[branchId]['general'] !== undefined) {
                return task.norms[branchId]['general']
            } else if (task.norms['general'] && task.norms['general']['general'] !== undefined) {
                return task.norms['general']['general']
            }

            return 0
        },
        // Получить нормы задачи для всех салонов
        getTaskNormForBranches(taskId: string): number[] {
            const branches = (this as any).planBranches
            return branches.map((branchId: string) => (this as any).getTaskNormForBranch(taskId, branchId))
        },
        // Проверяем, является ли метрика задачей плана (с префиксом task_ или без)
        isTaskMetric(metricId: string): boolean {
            if (!metricId) return false
            const normalizedId = metricId.startsWith('task_') ? metricId.replace('task_', '') : metricId
            const plan = (this as any).activePlan
            if (!plan || !plan.tasks) return false
            return plan.tasks.some((t: any) => t.id === normalizedId)
        },
        // Получить норму дневной выручки для конкретного салона
        getDailyRevenueNormForBranch(branchId: string): number {
            const plan = (this as any).activePlan
            if (!plan || !plan.dailyRevenuePlan) return 0

            if (plan.dailyRevenuePlan[branchId] !== undefined) {
                return plan.dailyRevenuePlan[branchId]
            } else if (plan.generalDailyRevenuePlan !== undefined) {
                return plan.generalDailyRevenuePlan
            }

            return 0
        },
        // Форматировать значение нормы
        formatNormValue(value: number): string {
            if (value === 0) return '—'
            return new Intl.NumberFormat('ru-RU', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(value)
        },
        // Форматировать итоговое значение начисления за период
        formatAccrualPeriodValue(metric: Metric): string {
            const periods = (this as any).periods
            if (!periods || periods.length === 0) return '—'

            // Суммируем все значения за все периоды
            const total = periods.reduce((sum: number, period: Period) => {
                const value = metric.values[period.id] || 0
                return sum + (typeof value === 'number' ? value : parseFloat(String(value)) || 0)
            }, 0)

            // Форматируем в зависимости от типа метрики
            if (metric.type === 'currency') {
                return new Intl.NumberFormat('ru-RU', {
                    style: 'currency',
                    currency: 'RUB',
                    minimumFractionDigits: 0,
                }).format(total)
            } else if (metric.type === 'percentage') {
                return `${total}%`
            } else {
                return new Intl.NumberFormat('ru-RU', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                }).format(total)
            }
        },
        // Подгонка высоты ячеек для строк "Салон"
        adjustRowHeights() {
            this.$nextTick(() => {
                const salonRows = document.querySelectorAll('.auto-height-row')

                salonRows.forEach((row: any) => {
                    const cells = row.querySelectorAll('td')
                    if (cells.length >= 2) {
                        const headerCell = cells[0] // Ячейка заголовка (Салон)
                        const dataCells = Array.from(cells).slice(1) // Ячейки с данными

                        // Сначала сбрасываем высоту ячейки заголовка
                        headerCell.style.height = 'auto'
                        headerCell.style.minHeight = 'auto'

                        // Находим максимальную высоту среди ячеек данных
                        let maxHeight = 0
                        dataCells.forEach((cell: any) => {
                            // Временно сбрасываем высоту ячейки для получения реальной высоты содержимого
                            const originalHeight = cell.style.height
                            cell.style.height = 'auto'

                            const cellHeight = cell.scrollHeight
                            if (cellHeight > maxHeight) {
                                maxHeight = cellHeight
                            }

                            // Восстанавливаем исходную высоту
                            cell.style.height = originalHeight
                        })

                        // Устанавливаем высоту ячейки заголовка равной максимальной высоте
                        if (maxHeight > 0) {
                            headerCell.style.height = maxHeight + 'px'
                            headerCell.style.minHeight = maxHeight + 'px'
                        }
                    }
                })
            })
        },
    },
    mounted() {
        // Подгоняем высоту ячеек после монтирования
        this.adjustRowHeights()

        // Добавляем обработчик изменения размера окна
        window.addEventListener('resize', this.adjustRowHeights)
    },

    beforeUnmount() {
        // Убираем обработчик при размонтировании компонента
        window.removeEventListener('resize', this.adjustRowHeights)
    },
    watch: {
        periods: {
            handler(newVal) {
                // Периоды изменились
            },
            immediate: true,
        },
        metrics: {
            handler(newVal) {
                // Метрики изменились - подгоняем высоту ячеек
                this.adjustRowHeights()
            },
            immediate: true,
        },
    },
})
